import React, { PureComponent } from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import moment from 'moment';
import PropTypes from 'prop-types';
import DocumentTitle from 'react-document-title';
import { Link } from 'react-router';
import html2pdf from 'html2pdf.js';
import htmlDocx from 'html-docx-js/dist/html-docx';
import FileSaver from 'file-saver';
import imageDataURI from 'image-data-uri';
import DOMPurify from 'dompurify';

import { makeGetCurrentUserId, makeGetResolvedDocUsers } from '../selectors/users';
import { makeGetProject } from '../selectors/projects';
import { makeGetNotificationsByAssociationId } from '../selectors/notifications';
import { makeGetDoc } from '../selectors/docs';
import { makeGetTheme } from '../selectors/app-props';
import { makeGetExternalAccountByProvider } from '../selectors/external-accounts';

import { readNotifications } from '../actions/notifications';
import { fetchDoc, updateDoc } from '../actions/docs';

import { debugLog as log } from 'shared-services/logger-service';
import { logError } from 'shared-services/logger-service';

import { getCleanIdFromUrl, delegateHover, copyUrlToClipboard, openWindow } from '../utils';
import { getDocLastUpdate, getDocPermission } from '../utils/doc';
import { getToolbarMenuItems } from '../utils/doc-editor';
import { buildAppUrl, corsFree } from '../utils/url';
import { isElectron } from 'services/env-service';
import { createAlert } from '../services/alert-service';
import { trackEvent, startTrackSession, stopTrackSession } from '../services/events-tracker-service';
import eventBus from '../services/event-bus';
import { browserHistory } from '../services/history';
import { getAccountToken } from '../api/external-accounts-api';

import AppLoading from './app-loading';
import DocEditor from './doc-editor';
import AvatarBase from './avatar-base';
import DropdownBtn from './dropdown-btn';
import CanUser from './can-user';
import Avatar from './avatar';
import Spinner from './spinner';
import ChatToggle from './chat-toggle';
import DocToolbar from './doc-toolbar';
import DocMenu from './doc-menu';
import DocShareDropdown from './doc-share-dropdown';
import GoogleDoc from './google-doc';
import DocumentUsers from './document-users';
import Tooltip from './tooltip';

import niftyLogo from '../images/nifty-logo-light.svg';

import IconShare from '../icons/icon-share';
import IconArrowsLeft from '../icons/icon-arrows-left';
import IconClose from '../icons/icon-close';

import Placeholder from './placeholders/document.placeholder';

import { SocketContext } from '../context/SocketContext';

const IMG_REGEX = /<img src="(http.*?)"/g;

const docStyles = `
    <style>
        .doc-canvas { font-family: 'SFProTextCustom', 'SFProText', 'Helvetica Neue', 'Helvetica', 'Arial'; font-size: 15px; line-height: 1.5; color: #2f2f2f; color: #000 !important; }
        .doc-canvas .doc-title { font-size: 2em; margin-bottom: .75em; margin-top: 0; color: #000; }

        .doc-canvas * { margin: 0; }

        .doc-canvas p,
        .doc-canvas ul,
        .doc-canvas ol,
        .doc-canvas dl,
        .doc-canvas hr,
        .doc-canvas table,
        .doc-canvas blockquote { margin-bottom: 1em; }

        .doc-canvas h1:last-child,
        .doc-canvas h2:last-child,
        .doc-canvas h3:last-child,
        .doc-canvas h4:last-child,
        .doc-canvas h5:last-child,
        .doc-canvas h6:last-child,
        .doc-canvas p:last-child,
        .doc-canvas ul:last-child,
        .doc-canvas ol:last-child,
        .doc-canvas dl:last-child,
        .doc-canvas table:last-child,
        .doc-canvas blockquote:last-child { margin-bottom: 0; }

        .doc-canvas h1 { font-size: 1.6em; margin-top: 1.5em; margin-bottom: .63em; font-weight: 700; }
        .doc-canvas h2 { font-size: 1.3em; margin-top: 1.5em; margin-bottom: .77em; font-weight: 700; }
        .doc-canvas h3 { font-size: 1.15em; margin-top: 1.5em; margin-bottom: .77em; font-weight: 700; }

        .doc-canvas h1:first-child,
        .doc-canvas h2:first-child,
        .doc-canvas h3:first-child { margin-top: 0; }

        .doc-canvas blockquote { position: relative; color: inherit; font-style: italic; font-weight: 500; padding: 0.63em 0 0 1.88em; border: 0 none; }
        .doc-canvas blockquote:before { content: ''; position: absolute; top: 0; left: 0; line-height: 1; background-image: url(data:image/png;base64,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); width: 3.44em; height: 2.75em; background-size: 100% 100%; }
        .doc-canvas ul,
        .doc-canvas ol { margin-left: 1.5em; }

        .doc-canvas table { width: 100%; border-collapse: collapse; border-spacing: 0; }
        .doc-canvas td { border: 1px solid; padding: .67em; }

        .doc-canvas code { font-family: Monaco, Menlo, Consolas, "Courier New", monospace; color: #d72b3f; padding: 0.13em .2em .07em; background-color: #f8f9f9; border: 1px solid #d6dadc; border-radius: .27em; }
        .doc-canvas pre code { display: block; padding: .67em; color: #2f2f2f; white-space: normal; }

        .doc-canvas .image-caption { font-weight: 500; font-style: italic; font-size: .73em; color: #adadad; text-align: center; margin-top: .67em; }
    </style>
`;


class Document extends PureComponent {
    static contextType = SocketContext;

    applyErrorAlert = createAlert('default', 'Error saving doc. We\'re restoring the last working version...', {
        hidden: true,
        duration: 0,
        noClose: true
    });

    constructor(props) {
        super(props);

        this.nameElement = null;

        this.unbindScrollerHover = null

        this.state = {
            name: this.props.initialName || '',
            content: '',
            focus: false,
            activeCommentsBlock: null,
            scrollPosition: 0
        };

        this.handleWindowUnload = this.handleWindowUnload.bind(this);
    }

    handleWindowUnload() {
        this.closeComponent();
    }

    handleMenuAction = type => {
        switch (type) {
            case 'undo':
                this.docCtrl && this.docCtrl.undo();
                break;

            case 'redo':
                this.docCtrl && this.docCtrl.redo();
                break;

            default: 
                this.docCtrl && this.docCtrl.toggleType(type);
        }
    }

    handleScroll = () => {
        this.setState({
            scrollPosition: this.canvasHolderElement.scrollTop / (this.canvasHolderElement.scrollHeight - this.canvasHolderElement.clientHeight) * 100
        });

        this.docCtrl && this.docCtrl.updateScrollPosition(this.canvasHolderElement.scrollTop);
    }

    handleShare = () => {
        this.shareCtrl.hide();
    }

    handleUtilClick = ({ payload }) => {
        switch (payload.action) {
            case 'archive':
                trackEvent('DOC.ARCHIVE', {
                    via: this.props.inModal ? 'Discussion' : 'Doc',
                    docName: this.props.docName
                });

                this.updateDoc({
                    archived: true
                });

                if (this.props.inModal) {
                    this.props.onArchive && this.props.onArchive();
                } else {
                    this.props.router.push(`${this.props.projectId}/documents`);
                }

                break;

            case 'unarchive':
                trackEvent('DOC.UNARCHIVE', {
                    via: this.props.inModal ? 'Discussion' : 'Doc',
                });

                this.updateDoc({
                    archived: false
                });
                break;

            case 'copyUrl':
                trackEvent('DOC.URL_COPY', {
                    via: this.props.inModal ? 'Discussion' : 'Doc',
                });

                copyUrlToClipboard(buildAppUrl(`/document/${this.props.doc.public_token}`));
                break;

            case 'exportPDF':
                this.exportPDF();
                break;

            case 'exportDOCX':
                this.exportDOCX();
                break;

            default:
                return null;
        }
    }

    handleNameBlur = () => {
        const name = this.nameElement.textContent;
        if (name !== this.props.docName) {
            this.updateDoc({
                name: this.state.name
            });
        }

        if (name !== this.state.name) {
            this.setState({
                name: this.nameElement.textContent
            });
        }
    }

    handleNameChange = () => {
        this.setState({
            name: this.nameElement.textContent || 'Untitled'
        }, () => {
            this.props.onChange && this.props.onChange();
        });
    }

    handleNameKeyDown = event => {
        switch (event.which) {
            case 13:
                event.preventDefault();

                setTimeout(() => {
                    this.docCtrl && this.docCtrl.focus(true);
                });
                break;

            default:
                break;
        }
    }

    handleBackClick = () => {
        this.props.router.push(`${this.props.projectId}/documents`);
    }

    convertDocImagesToBase64(docHtml) {
        const urls = [];
        let matches = [];

        while (matches) {
            matches = IMG_REGEX.exec(docHtml);
            if (matches) {
                urls.push(matches[1]);
            }
        }

        const uniqueUrls = [...new Set(urls)];

        return Promise
            .all(uniqueUrls.map(url => imageDataURI.encodeFromURL(corsFree(url))))
            .then(responses => {
                responses.forEach((base64, index) => {
                    docHtml = docHtml.replace(new RegExp(uniqueUrls[index], 'g'), base64);
                })

                return docHtml;
            });
    }

    getDocPrintHtml() {
        if (!this.docCtrl) {
            return '';
        }

        return this.convertDocImagesToBase64(this.docCtrl.getHTML(this.props.docName)).then(docHtml => docStyles + docHtml);
    }

    exportDOCX() {
        if (this.googleType) {
            this.download(`https://docs.google.com/${this.googleType}/d/${this.props.doc.external_id}/export?format=docx`);
            return;
        }
        const alert = createAlert('success', 'Export in progress...');
        this.getDocPrintHtml().then(docHtml => {
            const fileBlob = htmlDocx.asBlob(docHtml);
            const save = () => {
                FileSaver.saveAs(fileBlob,`${this.props.docName}.docx`);
            }
            save();

            alert.setType('success').setContent(<div>Export in progress... <u onClick={save} style={{cursor: 'pointer'}}>Click here if download hasn't started.</u></div>);
        });
    }

    exportPDF() {
        log('[DOC] EXPORTING TO PDF');
        if (this.googleType) {
            this.download(`https://docs.google.com/${this.googleType}/d/${this.props.doc.external_id}/export?format=pdf`);
            return;
        }
        const alert = createAlert('success', 'Export in progress...');

        this.getDocPrintHtml().then(docHtml => {
            log('[DOC] Export to pdf (html)', docHtml);
            const doc = document.createElement('div');
            doc.innerHTML = docHtml;
            const result = html2pdf()
                .from(doc)
                .set({
                    filename: `${this.props.docName}.pdf`,
                    margin: 15
                });

            const save = () => result.save();

            alert.setType('success').setContent(<div>Export in progress... <u onClick={save} style={{cursor: 'pointer'}}>Click here if download hasn't started.</u></div>);

            save();
        });
    }

    download(url) {
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('target', '_blank');

        link.click();
    }

    trackView() {
        if (this.documentToken) {
            trackEvent('DOC.VIEW', {
                via: 'Doc Share URL',
                id: this.props.id,
                name: this.props.docName
            });
        } else {
            trackEvent('DOC.VIEW', {
                via: this.props.inModal ? 'Discussion' : 'Docs',
                id: this.props.id,
                name: this.props.docName
            });
        }
    }

    onLoad() {
        this.props.onLoad && this.props.onLoad();
    }

    getCursorElement(userId) {
        const user = (this.props.doc.invitees || []).find(user => user.id === userId);

        if (!user) {
            return null;
        }

        const userCursorCard = (
            <span className="user-cursor-card">
                <span className="card-image">
                    <Avatar
                        image={user.image}
                        color={user.color}
                        text={user.initials}
                        size={'xxsmall'}
                    />
                </span>

                <span className="card-body">
                    {user.name.split(' ')[0]} is typing…
                </span>
            </span>
        )

        const userCursorCardHolder = document.createElement('span');

        ReactDOM.render(
            userCursorCard,
            userCursorCardHolder
        );

        return userCursorCardHolder;
    }

    bindCursorElements(cm) {
        const scrollerElement = cm.getScrollerElement();

        this.unbindScrollerHover = delegateHover(scrollerElement, 'other-user-cursor', event => {
            const target = event.target;
            const parentBoundingRect = scrollerElement.getBoundingClientRect();
            const elementBoundingRect = target.getBoundingClientRect();

            if (!target.querySelector('.user-cursor-card')) {
                const userMatch = target.className.match(/other-user-cursor-(.+)\s/);
                if (!userMatch) {
                    return;
                }
                const cursorElement = this.getCursorElement(userMatch[1]);

                if (!cursorElement) {
                    return;
                }

                target.appendChild(cursorElement);
            }

            if (parentBoundingRect.right < elementBoundingRect.right + 200) {
                target.classList.add('close-to-right-edge');
            } else {
                target.classList.remove('close-to-right-edge');
            }
        });
    }

    updateDoc(props) {
        if (!this.props.id) {
            return;
        }

        updateDoc(this.props.id, props);
    }

    closeComponent() {
        stopTrackSession('Document');
    }

    handleCommentsThreadRemove = threadId => {
        this.props.dispatch({
            type: 'MESSAGES_REMOVE_REQUESTED',
            payload: {
                threadId
            }
        });
    }

    handleGoogleDocPermissionChangeClick = () => {
        this.setState({
            googlePermissionsLoading: true
        });

        getAccountToken('google', !this.props.googleAccount.team)
            .then(({ token }) => {
                if (isElectron()) {
                    openWindow(buildAppUrl(`/google-drive-share?token=${token}&doc_id=${this.props.doc.external_id}`), {
                        width: 600,
                        height: 600
                    }, 'drive-share');

                    this.setState({
                        googlePermissionsLoading: false
                    });
                } else {
                    window.gapi.load('drive-share', () => {
                        const s = new window.gapi.drive.share.ShareClient();
                        s.setOAuthToken(token);
                        s.setItemIds([this.props.doc.external_id]);
                        s.showSettingsDialog();

                        this.setState({
                            googlePermissionsLoading: false
                        });
                    });
                }
            });
    }

    handleDocReload = () => {
        this.leaveDoc();
        this.setState({
            reloading: true
        }, () => {
            setTimeout(() => {
                this.joinDoc();
                this.setState({
                    reloading: false
                });
            }, 500);
        })
    }

    get documentToken() {
        return (this.props.routeParams && this.props.routeParams.documentToken);
    }

    get hasName() {
        const name = this.state.name;
        return (name && name !== 'Untitled');
    }

    get lastEditedMessage() {
        if (!this.props.doc || !this.props.doc.members) {
            return null;
        }

        const lastEdited = getDocLastUpdate(this.props.doc.members, this.props.doc.author, this.props.doc.created_at);

        if (new Date() - new Date(lastEdited.date) > 60000) {
            return `Last Edited: ${moment(lastEdited.date).format('MMM D, YYYY, h:mma')}`;
        } else {
            return 'Updated now';
        }
    }

    get shareItems() {
        const items = [];

        items.push({
            material: 'screen_share',
            text: 'Export to .pdf',
            payload: {
                action: 'exportPDF'
            }
        });

        items.push({
            material: 'screen_share',
            text: 'Export to .docx',
            payload: {
                action: 'exportDOCX'
            }
        });

        items.push({
            material: 'content_copy',
            text: 'Copy Public URL',
            payload: {
                action: 'copyUrl'
            }
        });

        return items;

    }

    get utilItems() {
        const items = [];

        if (this.currentUserCanEdit) {
            if (this.props.doc.archived) {
                items.push({
                    text: 'Unarchive',
                    payload: {
                        action: 'unarchive'
                    }
                });
            } else {
                items.push({
                    text: 'Archive',
                    payload: {
                        action: 'archive'
                    },
                    prompt: {
                        title: 'ARCHIVE DOC',
                        text: 'Do you really want to archive this doc?',
                        confirmText: 'Archive',
                        confirmClassName: 'btn-caution'
                    }
                });
            }
        }

        return items;
    }

    get currentUserCanEdit() {
        return this.permission === 'edit';
    }

    get currentUserCanComment() {
        return this.permission === 'comment';
    }

    get readOnly() {
        return this.props.archived || this.props.readOnly || (!this.currentUserCanEdit && this.props.id) || this.documentToken || (this.props.doc && this.props.doc.archived);
    }

    get useFullHeader() {
        return !this.props.routeParams || !this.props.project;
    }

    get inModal() {
        return this.props.inModal;
    }

    get fromSharedUrl() {
        return !!this.documentToken;
    }

    get docActions() {
        if (!this.props.id) {
            return null;
        }

        return (
            <div className="header-utils">
                {
                    !this.props.inModal && this.googleType ? (
                        <CanUser type="viewChat">
                            <div className="util">
                                <ChatToggle />
                            </div>
                        </CanUser>
                    ) : null
                }

                {
                    this.googleType ? (
                        <div className="util">
                            {
                                this.currentUserCanEdit && (
                                    this.props.googleAccount ? (
                                        <button className={'btn btn-small btn-secondary btn-border btn-has-loading' + (this.state.googlePermissionsLoading ? ' loading' : '')} disabled={this.state.googlePermissionsLoading} onClick={this.handleGoogleDocPermissionChangeClick}>
                                            <span className="text-condensed">
                                                Edit Doc Permissions
                                            </span>
                                        </button>
                                    ) : (
                                        <Link
                                            to={{
                                                pathname: `settings/apps`,
                                                state: {
                                                    closeUrl: browserHistory.getCurrentLocation().pathname
                                                }
                                            }}
                                        >
                                            <Tooltip tooltip="Link your Google account first">
                                                <button className={'btn btn-small btn-secondary btn-border btn-has-loading' + (this.state.googlePermissionsLoading ? ' loading' : '')}>
                                                    <span className="text-condensed">
                                                        Edit Doc Permissions
                                                    </span>
                                                </button>
                                            </Tooltip>
                                        </Link>
                                    )
                                )
                            }
                        </div>
                    ) : null
                }

                <div className="util divided">
                    <DocumentUsers id={this.props.id} size="small" />
                </div>

                <div className="util">
                    <DropdownBtn btnClassName="btn btn-simple btn-small btn-square btn-icon" onItemClick={this.handleUtilClick} items={this.shareItems}>
                        <IconShare size="14px" />
                    </DropdownBtn>

                    <DocShareDropdown docId={this.props.id} provideController={ctrl => this.shareCtrl = ctrl} onShare={this.handleShare} />
                </div>

                {
                    this.utilItems.length ? (
                        <div className="util">
                            <DropdownBtn btnClassName="btn btn-simple btn-small btn-square btn-icon" onItemClick={this.handleUtilClick} items={this.utilItems}>
                                <i className="material-icons">more_horiz</i>
                            </DropdownBtn>
                        </div>
                    ) : null
                }

            </div>
        )
    }

    get permission() {
        if (!this.props.id && !this.documentToken) {
            return 'edit';
        }

        if (!this.props.doc || !this.props.doc.members) {
            return null;
        }

        return getDocPermission(this.props.doc.members, this.props.currentUserId);
    }

    get googleType() {
        return this.props.doc && this.props.doc.subtype;
    }

    async attemptFetch(force) {
        if ((!this.props.id && !this.documentToken) || (this.props.doc && this.props.doc.loading)) {
            return;
        }

        if (!this.props.doc || (!this.props.doc.content && !this.googleType) || force) {
            const doc = await fetchDoc(this.props.id || this.documentToken, !Boolean(this.props.id));

            if (this.documentToken && doc.hasAccess) {
                this.props.router.replace(`/${doc.project}/documents/${doc.id}`);
            }
        }
    }

    joinDoc() {
        if (this.context && this.context.emitEvent) {
            this.context.emitEvent('join_document', {
                id: this.props.id,
                member_id: this.props.currentUserId
            });
        } else {
            logError('nifty-doc', new Error(`${this.context ? 'context.emitEvent' : 'context'} isn't available in doc`));
        }
    }

    leaveDoc() {
        this.context && this.context.emitEvent && this.context.emitEvent('leave_document', {
            id: this.props.id,
            member_id: this.props.currentUserId
        });
    }

    componentDidMount() {
        if (this.currentUserCanEdit) {
            this.nameElement && this.nameElement.focus();
        }

        if (this.props.id) {
            this.joinDoc();
        }

        this.attemptFetch(true);

        this.setState({
            name: this.props.docName || this.props.initialName || ''
        });

        this.props.provideController && this.props.provideController({
            isPopulated: () => {
                return this.docCtrl && this.docCtrl.isPopulated();
            },
            getContent: () => {
                return this.docCtrl && this.docCtrl.getValue();
            },
            getName: () => {
                return this.state.name || this.props.docName;
            },
            isReadOnly: () => {
                return this.readOnly;
            },
            getEditor: () => {
                return this.docCtrl && this.docCtrl.getEditor();
            }
        });

        if (this.props.doc) {
            this.trackView();
        }

        if (this.props.doc && this.props.doc.content) {
            this.onLoad();
        }

        startTrackSession('Document');

        // Tour event
        eventBus.emitEvent('doc:open');

        window.addEventListener('beforeunload', this.handleWindowUnload);
    }

    componentDidUpdate(prevProps) {
        const notifications = this.props.notifications.filter(notification => !notification.comment_id);

        if (notifications.length) {
            readNotifications(notifications.map(notification => notification.id));
        }

        if (this.props.id !== prevProps.id) {
            if (prevProps.id) {
                this.leaveDoc();
            }

            if (this.props.id) {
                this.joinDoc();
                this.attemptFetch();
            }
        }

        if ((this.props.doc && !prevProps.doc) || (this.props.doc && prevProps.doc && !prevProps.doc.content && this.props.doc.content)) {
            this.setState({ value: this.props.doc.content });
        }

        if (this.props.docName !== prevProps.docName  && this.props.docName !== this.state.name) {
            this.setState({ name: this.props.docName });
        }

        if (this.props.doc && prevProps.doc && !this.props.doc.loading) {
            this.trackView();

            if (this.props.doc.applyError && !prevProps.doc.applyError) {
                this.applyErrorAlert.setContent('The doc is corrupted. We\'re restoring the last working version...').setType('default').show();
                fetchDoc(this.props.id || this.documentToken);
            } else if (!this.props.doc.applyError && prevProps.doc.applyError) {
                this.applyErrorAlert.setContent('Doc restored!').setType('success').hide(2000);
            }
        }

        if (this.props.doc && this.props.doc.content && (!prevProps.doc || !prevProps.doc.content)) {
            this.onLoad();
            this.setState({ value: this.props.doc.content });
        }
    }

    componentWillUnmount() {
        this.leaveDoc();
        this.closeComponent();
        this.props.provideController && this.props.provideController(null);
        this.unbindScrollerHover && this.unbindScrollerHover();
        window.removeEventListener('beforeunload', this.handleWindowUnload);

        // Tour event
        eventBus.emitEvent('doc:close');
    }

    render() {
        if (this.documentToken && !this.props.doc) {
            return <AppLoading />
        }

        if (this.props.id && (!this.props.doc || this.props.doc.loading)) {
            return <Placeholder />;
        }

        const docName = this.props.docName || '';

        return (
            <DocumentTitle title={`${this.props.docName || 'Untitled Document'} | Nifty`}>
                <div className={'document-editor' + (this.currentUserCanEdit ? ' can-edit' : '') + (this.fromSharedUrl ? ' shared-doc' : '')}>
                    <style>{`[data-comment-key="${this.state.activeCommentsBlock}"], [data-comment-key="${this.state.hoveredCommentsBlock}"] { background-color: #ffede0; }`}</style>

                    {
                        !this.fromSharedUrl ? (
                            <div className={'header document-editor-header' + (this.props.theme === 'light' ? ' header-accent' : '')}>
                                {
                                    this.inModal ? (
                                        <div className="header-centered-main">
                                            {
                                                this.props.creating ? (
                                                    <React.Fragment key="creating">
                                                        <h4>Create a Document</h4>

                                                        <p>All project members will be able to view and comment on this doc by default.</p>
                                                    </React.Fragment>
                                                ) : (
                                                    <React.Fragment key="existing">
                                                        <h4>{this.state.name}</h4>
                                                    </React.Fragment>
                                                )
                                            }
                                        </div>
                                    ) : (
                                        <div className="header-main">
                                            <div className="header-back" onClick={this.handleBackClick}>
                                                <span className="header-back-arrow">
                                                    <IconArrowsLeft />
                                                </span>

                                                <div className="header-back-avatar">
                                                    <AvatarBase
                                                        size="small"
                                                        image={this.props.project.logo}
                                                        text={this.props.project.initials}
                                                        color={this.props.project.color}
                                                        secondaryColor={this.props.project.secondary_color}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )
                                }

                                {
                                    this.googleType ? null : (
                                        <DocToolbar>
                                            <DocMenu
                                                activeItems={getToolbarMenuItems(this.permission)}
                                                items={getToolbarMenuItems('edit')}
                                                horizontal={true}
                                                onAction={this.handleMenuAction}
                                                value={this.state.value}
                                                editor={this.docCtrl && this.docCtrl.getEditor()}
                                            />
                                        </DocToolbar>
                                    )
                                }

                                <div className="header-right">
                                    { this.docActions }
                                </div>
                            </div>
                        ) : null
                    }

                    <div className="document-scroll-proggress">
                        <span style={{ width: `${this.state.scrollPosition}%` }} />
                    </div>

                    <div className="document-editor-main">
                        {
                            this.googleType ? (
                                <GoogleDoc docType={this.googleType} googleId={this.props.doc.external_id} />
                            ) : (
                                <div className="document-editor-canvas-holder" ref={element => this.canvasHolderElement = element} onScroll={this.handleScroll}>
                                    <div className="document-editor-canvas" ref={element => this.canvasElement = element}>
                                        {
                                            (this.props.preview && !this.currentUserCanEdit) ? null : (
                                                <div className="document-editor-title">
                                                    <span
                                                        className={'doc-field-input' + (!this.hasName ? ' empty' : '')}
                                                        ref={element => {this.nameElement = element}}
                                                        contentEditable={this.currentUserCanEdit}
                                                        placeholder="Give it a name"
                                                        dangerouslySetInnerHTML={{ __html: docName === 'Untitled' ? '' : DOMPurify.sanitize(docName) }}
                                                        onInput={this.handleNameChange}
                                                        onBlur={this.handleNameBlur}
                                                        onKeyDown={this.handleNameKeyDown}
                                                    />
                                                </div>
                                            )
                                        }

                                        <div className={'document-editor-content-holder'}>
                                            <Spinner />

                                            <div className="document-editor-content">
                                                {
                                                    !this.state.reloading && ((!this.props.id && !this.documentToken) || (this.props.doc && this.props.doc.content)) ? (
                                                        <DocEditor
                                                            id={this.props.id || (this.props.doc && this.props.doc.id)}
                                                            value={this.props.doc && this.props.doc.content}
                                                            currentUserId={this.props.currentUserId}
                                                            name={this.props.docName}
                                                            threads={this.props.comments}
                                                            onChange={this.props.onChange}
                                                            permission={this.permission}
                                                            notifications={this.props.notifications}
                                                            readOnly={this.props.doc && this.props.doc.applyError}
                                                            showToc={true}
                                                            useUrlsForFiles={Boolean(this.documentToken)}
                                                            docMembers={this.props.docMembers}
                                                            onCommentsThreadRemove={this.handleCommentsThreadRemove}
                                                            onReloadDoc={this.handleDocReload}
                                                            provideController={ctrl => this.docCtrl = ctrl}
                                                        />
                                                    ) : null
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )
                        }

                        {
                            !this.props.inModal && !this.googleType ? (
                                <CanUser type="viewChat">
                                    <div className="document-chat-toggle">
                                        <ChatToggle />
                                    </div>
                                </CanUser>
                            ) : null
                        }

                        {
                            !this.googleType && this.lastEditedMessage ? (
                                <div className="document-last-edited">
                                    <i className="icon material-icons">update</i> {this.lastEditedMessage}
                                </div>
                            ) : null
                        }
                    </div>
                </div>
            </DocumentTitle>
        );
    }
}

const makeMapStateToProps = () => {
    const getCurrentUserId = makeGetCurrentUserId();
    const getProject = makeGetProject();
    const getNotificationsByAssociationId = makeGetNotificationsByAssociationId();
    const getTheme = makeGetTheme();
    const getDoc = makeGetDoc();
    const getResolvedDocUsers = makeGetResolvedDocUsers();
    const getExternalAccountTeamByProvider = makeGetExternalAccountByProvider();
    const getExternalAccountUserByProvider = makeGetExternalAccountByProvider();

    return ({ projects, docs, users, teams, messages, notifications, appProps, externalAccounts }, ownProps) => {
        const project = getProject(projects.items, ownProps.projectId);
        const docId = ownProps.docId || (ownProps.routeParams && getCleanIdFromUrl(ownProps.routeParams.documentId));
        const doc = (docId || (ownProps.routeParams && ownProps.routeParams.documentToken)) && getDoc(docs.items, docId || (ownProps.routeParams && ownProps.routeParams.documentToken));

        const teamAccount = getExternalAccountTeamByProvider(externalAccounts.items, 'google', true);
        const userAccount = getExternalAccountUserByProvider(externalAccounts.items, 'google');

        return {
            id: docId,
            currentUserId: getCurrentUserId(users),
            doc,
            docName: doc && doc.name,
            project,
            // TODO: Impvove
            comments: messages.items,
            notifications: getNotificationsByAssociationId(notifications.items, docId),
            docMembers: (ownProps.routeParams && ownProps.routeParams.documentToken) ? [] : getResolvedDocUsers(doc, users),
            theme: getTheme(appProps),
            googleAccount: teamAccount || userAccount
        }
    };    
}

export default connect(makeMapStateToProps)(Document);
